import React from "react";
import {
  Button,
  Card,
  CardStackSection,
  Heading,
  Stack,
  TextField,
  Text,
  TextLink,
  Alert,
  Box,
} from "@origin-digital/ods-core";
import ReadOnlyTextField from "../FormComponents/ReadOnlyTextField";
import { DatePickerProvider, DatePicker } from "@origin-digital/ods-pickers";
import ReCAPTCHA from "react-google-recaptcha";
import envConfig from "src/envConfig/envHelper";
import RequestDetailsForm from "./PublicFormRequestDetails";
import { isUnavailableDateForFuture } from "../helper";

export type AddressLookupResponse = {
  address?: string;
  service_identifier?: string;
  fee_scripting?: string;
  db_fee?: number;
  mp_fee?: number;
};

export interface FormData {
  journeyId: string;
  postcode: string;
  scheduleDate: Date | null;
  meterNumber: string;
  nmi?: string;
  mirn?: string;
}

export interface FormErrors {
  journeyId?: string;
  postcode?: string;
  meterNumber?: string;
  scheduleDate?: string;
}

type RemoveSupplyPublicFormProps = {
  address: string;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  errors: FormErrors;
  touched: Record<string, boolean>;
  showError: boolean;
  loading: boolean;
  handleJourneyIdChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePostcodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleJourneyIdBlur: () => void;
  handlePostcodeBlur: () => void;
  handleCheckDetails: () => void;
  handleFinalSubmit: () => void;
  agreement: React.ReactNode;
  recaptchaRef: React.RefObject<ReCAPTCHA>;
  nmiOrMirnField: React.ReactNode;
  instructionText: React.ReactNode;
};

const RemoveSupplyPublicForm: React.FC<RemoveSupplyPublicFormProps> = ({
  address,
  formData,
  setFormData,
  errors,
  touched,
  showError,
  loading,
  handleJourneyIdChange,
  handlePostcodeChange,
  handleJourneyIdBlur,
  handlePostcodeBlur,
  handleCheckDetails,
  handleFinalSubmit,
  agreement,
  recaptchaRef,
  nmiOrMirnField,
  instructionText,
}) => {
  return (
    <CardStackSection>
      <Card>
        {/* Outer container */}
        <Stack space={["large", "xlarge"]}>
          {/* Sections */}
          <Stack space={["xlarge", "xxlarge"]}>
            <Stack>
              <Heading variant="h3">Request to remove energy supply</Heading>
              <Text>{instructionText}</Text>
              <Text>
                If you did not request this work, please contact us ASAP on{" "}
                <TextLink href="tel:132 461">132 461</TextLink>.
              </Text>
            </Stack>
            {/* Request details */}
            <Stack space="large">
              <Heading variant="h4">Request details</Heading>
              {address ? (
                <Stack space={["xlarge", "xxlarge"]}>
                  <Stack space={["large", "xlarge"]}>
                    <ReadOnlyTextField
                      label="Reference number"
                      value={formData.journeyId}
                    />
                    <Stack space="small">
                      <Alert tone="caution">
                        Please check if the site address is correct before
                        proceeding.
                      </Alert>
                      <ReadOnlyTextField label="Address" value={address} />
                    </Stack>
                    {nmiOrMirnField}
                  </Stack>
                  {/* Meter number */}
                  <TextField
                    id="meterNumber"
                    variant="outlined"
                    label="Meter number"
                    hideHelperTextSpace={false}
                    helperText={
                      touched.meterNumber && errors.meterNumber
                        ? errors.meterNumber
                        : "The meter number can be found on your latest bill."
                    }
                    value={formData.meterNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, meterNumber: e.target.value })
                    }
                    error={Boolean(touched.meterNumber && errors.meterNumber)}
                  />
                  {/* End meter number */}
                  {/* Abolishment Date */}
                  <DatePickerProvider>
                    <DatePicker
                      helperText={
                        touched.scheduleDate && errors.scheduleDate
                          ? errors.scheduleDate
                          : "Please confirm the date when the site will be vacant or safe to abolish. The abolishment may occur anytime between this date and up to 20 business days thereafter."
                      }
                      id="datepicker"
                      label="Date for removal of supply"
                      value={formData.scheduleDate}
                      onChange={(date) => {
                        if (date != formData.scheduleDate) {
                          setFormData({ ...formData, scheduleDate: date });
                        }
                      }}
                      shouldDisableDate={isUnavailableDateForFuture}
                      muiProps={{
                        slotProps: {
                          textField: {
                            variant: "outlined",
                            error: Boolean(
                              touched.scheduleDate && errors.scheduleDate
                            ),
                          },
                        },
                      }}
                    />
                  </DatePickerProvider>
                  {/* Abolishment Date */}
                  {/* Agreement */}
                  <Stack space="large">
                    <Heading variant="h4">Confirm and accept</Heading>
                    {agreement}
                  </Stack>
                  {/* End Agreement */}
                </Stack>
              ) : (
                <RequestDetailsForm
                  formData={formData}
                  errors={errors}
                  touched={touched}
                  showError={showError}
                  handleJourneyIdChange={handleJourneyIdChange}
                  handlePostcodeChange={handlePostcodeChange}
                  handleJourneyIdBlur={handleJourneyIdBlur}
                  handlePostcodeBlur={handlePostcodeBlur}
                />
              )}
            </Stack>
            {/* End Request details */}
            {/* Agreement will appear here */}
          </Stack>
          {/* End Sections */}
          {/* Actions  */}
          <Stack space="large" alignX="center">
            {address ? (
              <Button
                fullWidth={true}
                onClick={handleFinalSubmit}
                spinner={loading}
              >
                Accept & submit
              </Button>
            ) : (
              <Button
                variant="outlined"
                fullWidth={true}
                onClick={handleCheckDetails}
                spinner={loading}
              >
                Check details
              </Button>
            )}
          </Stack>
          {/* End Actions */}
        </Stack>
        {/* End Outer container */}
      </Card>
      {address ? (
        <Box
          width="full"
          backgroundColor="transparent"
          paddingX={{ xs: "medium", sm: "none" }}
          paddingY={["xsmall", "none"]}
        >
          <Text variant="body-small">
            This site is protected by reCAPTCHA and the{" "}
            <TextLink
              href="https://policies.google.com/privacy"
              target="_blank"
            >
              Google Privacy Policy
            </TextLink>{" "}
            and{" "}
            <TextLink href="https://policies.google.com/terms" target="_blank">
              Terms of Service apply
            </TextLink>
            .
          </Text>
        </Box>
      ) : (
        <Box />
      )}
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={envConfig.config.reCaptchaSiteKey}
      />
    </CardStackSection>
  );
};

export default RemoveSupplyPublicForm;
